import type { View, ViewContext } from '../router';
import { el, ensureStyle } from '../dom';
import { icon } from '../components/icons';
import {
  backBtn,
  emptyFocus,
  exitButton,
  ensureQuizStyle,
  fillProgress,
  focusSetup,
  focusTag,
  keyLegend,
  lengthField,
  newBirdsField,
  missedList,
  quizHeader,
  quizKeys,
  retryTag,
  afterBar,
  tagSlot,
  compareButton,
  dontKnowButton,
  undoButton,
  segmented,
  type HearHandle,
  type KeyActions,
  type Miss,
} from '../components/quiz-chrome';
import { photoCredit, speciesPhoto } from '../components/species-photo';
import { getStripView } from '../prefs';
import { waveformPlayer, type WaveformHandle } from '../components/waveform-player';
import {
  buildSoundMatchSession,
  soundMatchPool,
  type SoundMatchQuestion,
} from '../../core/quiz/sound-match';
import { focusCodes, narrow, parseFocus } from '../../core/quiz/focus';
import { WITHOUT_CALLS } from '../../core/quiz/sound';
import { getSpecies } from '../../core/region';
import { studyCodes } from '../../core/ignored';
import { imageUrl } from '../../core/media/image';
import { ratingFromCorrect } from '../../core/fsrs';
import { clearUndo, gradeAnswer, undoAnswer } from '../../core/undo';
import { reaskMatch } from '../../core/quiz/retry';
import { haptic } from '../haptics';
import {
  newBirdShare,
  retryMisses,
  ROUND_LENGTHS,
  roundLength,
  setNewBirdShare,
  setRoundLength,
  setSoundWithCalls,
  soundWithCalls,
} from '../../core/quiz/settings';
import {
  HARD_SOUND_MASTERY,
  masteryBySkill,
  soundHardUnlocked,
  XP_PER_CORRECT,
} from '../../core/progress';
import { t } from '../../strings';

ensureQuizStyle();

ensureStyle(
  'quiz-sound-match-css',
  `
  /* A sound mode, so the setup screen's chosen option takes the sound hue. */
  .smatch { --seg-on: var(--skill-sound); }
  /* The bird, named under its own picture. The photo alone would make every
     question two questions — recognise the bird, then place its voice — and
     grade the miss against a sound card the learner may know perfectly well. */
  .smatch .who { text-align:center; margin-top:-6px; }
  .smatch .who .nm { font-family:var(--font-display);
    font-variation-settings:var(--fvs-display); font-size:23px; line-height:1.15;
    display:block; }
  .smatch .who .sci { font-style:italic; font-size:12.5px; color:var(--ink-soft); }
  .smatch .opts { display:flex; flex-direction:column; gap:12px; }
  .smatch .opt { display:flex; flex-direction:column; gap:5px; }
  /* The letter and the strip side by side: the letter is the ANSWER and the
     strip is the sound. Two controls, never one — a strip you both play and
     answer with would grade a question the moment you tried to listen to it. */
  .smatch .row { display:flex; align-items:stretch; gap:9px; }
  .smatch .row .waveform { flex:1; min-width:0; }
  .smatch .pick { flex:none; width:58px; padding:0;
    font-family:var(--font-display); font-variation-settings:var(--fvs-display);
    font-size:24px; line-height:1; }
  /* The same four states the name lists wear, and for the same reason: the pick
     you got right, the pick you got wrong, and the one you should have had. */
  .smatch .pick.correct { background:var(--moss); color:var(--on-dark);
    border-color:var(--honey-soft); }
  .smatch .pick.wrong { background:var(--danger); color:var(--on-danger);
    border-color:transparent; }
  .smatch .pick.answer { background:var(--moss-pale); color:var(--on-moss-pale);
    border-color:var(--moss); }
  .smatch .pick.dim { opacity:.45; }
  .smatch .pick:disabled { opacity:1; }
  .smatch .pick.dim:disabled { opacity:.45; }
  /* Who each clip actually was, revealed with the answer — the decoy's name is
     most of the lesson, since it is the bird this one is likeliest to be taken
     for. The row is reserved rather than inserted, so nothing below the strips
     moves when the answer lands. */
  .smatch .nmrow { display:flex; align-items:center; gap:6px; min-height:19px;
    padding-left:67px; visibility:hidden; }
  .smatch .opt.shown .nmrow { visibility:visible; }
  .smatch .nmrow .nm { font-family:var(--font-display); font-size:14.5px;
    line-height:1.2; }
  .smatch .nmrow.right { color:var(--moss); }
  .smatch .nmrow.missed { color:var(--danger); }
  .smatch .nmrow.other { color:var(--ink-soft); }
  .smatch .nmrow .icon { flex:none; }
  /* Both strips stay live after the answer: hearing them back to back, now that
     you know which was which, is the whole exercise.
     
     The badges that used to share this line ride the header row now — see
     .bar .tags in the shared chrome. A photo, a name, two sound strips and
     four controls is already the tallest question in the app; a centred row
     carrying two words was the cheapest thing on it to give up. */
  .smatch .hardtag { color:var(--accent); border:1.5px solid currentColor;
    border-radius:999px; padding:3px 8px; font-size:8.4px; letter-spacing:.1em;
    line-height:1; flex:none; }
  /* Two sound strips is a lot of height to find under a photo. The hero gives
     up more here than it does in Photo ID, where it IS the question. */
  .smatch .hero { max-height:min(34vh, 280px); }
  @media (max-height:740px) {
    .smatch .hero { height:min(20vh, 140px); }
    .smatch .who .nm { font-size:20px; }
    .smatch .opts { gap:9px; }
  }
  /* Spectrogram strips stand 108px to a waveform's 72, and this mode shows two
     of them — 72px more than the screen was measured with. On a 667pt phone
     that is the difference between Next sitting above the nav bar and sitting
     behind it, so the tall rendering buys its own height back out of the photo
     and the gaps. The class is set from the strip preference rather than by
     matching on the strip itself, since the frame is styled before the strips
     inside it exist. */
  @media (max-height:740px) {
    .smatch.tall { gap:10px; }
    .smatch.tall .hero { height:min(13vh, 92px); }
    .smatch.tall .who .nm { font-size:18px; }
  }
`,
);

/** What a round is allowed to play. `all` is the hard mode: calls included. */
type Voices = 'songs' | 'all';

const VOICES: { value: Voices; label: string; note: string }[] = [
  {
    value: 'songs',
    label: t('quiz.voices.songs'),
    note: t('quiz.voices.songsNote'),
  },
  { value: 'all', label: t('quiz.voices.all'), note: t('quiz.voices.allNote') },
];

/** The letters the two clips answer to. */
const LETTERS = ['A', 'B'];

export const quizSoundMatchView: View = async (root: HTMLElement, ctx: ViewContext) => {
  // Sound Match plays recordings, so it answers to the same "are calls in the
  // mix" preference Sound ID does — shared rather than duplicated, because the
  // question it settles is about the learner's ear and not about the mode.
  const unlocked = soundHardUnlocked(await masteryBySkill(studyCodes()));
  const [callsDefault, savedLen, savedShare] = await Promise.all([
    soundWithCalls(unlocked),
    roundLength('match'),
    newBirdShare('match'),
  ]);
  let voices: Voices = callsDefault ? 'all' : 'songs';
  let len = savedLen;
  // How much of the round is held for birds this learner has never met, as a
  // share of its length; see core/quiz/settings.ts.
  let share = savedShare;
  // Whether the round IN PROGRESS has calls in it — fixed when it was built, so
  // the tag on screen can never disagree with what is playing.
  let hard = voices === 'all';
  // A round started from the Library is confined to the birds that were on
  // screen when its button was pressed — see core/quiz/focus.ts. The focus lives
  // in the hash and nowhere else, so a reload keeps it and leaving drops it.
  const focus = parseFocus(ctx.params.focus);
  const pool = focus ? focusCodes(focus) : null;

  // Two strips, and the spectrogram rendering is half again as tall as the
  // waveform — enough on a short phone to decide whether the round's own button
  // is on the screen. See `.smatch.tall` in the CSS above.
  const wrap = el(`div.quiz.smatch${getStripView() === 'spectrogram' ? '.tall' : ''}`, {});
  root.append(wrap);

  let session: SoundMatchQuestion[] = [];
  // How many questions the round was dealt. Anything past it is a miss come
  // back for another go — see `retryMisses` — and is practice, not scored.
  let size = 0;
  let index = 0;
  let retryOn = false;
  let correctCount = 0;
  let missed: Miss[] = [];
  let disposed = false;
  // Both strips of the question on screen, so leaving it can silence them.
  let players: WaveformHandle[] = [];
  // And the speakers on the end-of-round Missed panel, silenced the same way.
  let missSpeakers: HearHandle[] = [];

  // One key listener for the whole visit, reading whatever the screen currently
  // offers. A listener added per render is a listener leaked per render.
  let keys: KeyActions | null = null;
  const releaseKeys = quizKeys(wrap, () => keys);

  // Only the first round of a visit inherits a hand-off from elsewhere.
  let firstRound = true;

  function hush(): void {
    for (const p of players) p.stop();
    players = [];
    for (const s of missSpeakers) s.stop();
    missSpeakers = [];
  }

  // `?go=1` — a round started from the Home card, which already knows what the
  // last one was set to. The setup screen is three taps between a learner and
  // the round they have already asked for; "Change options" at the end of the
  // round is the way back to one.
  if (ctx.params.go) void start();
  else renderSetup();

  function renderSetup(): void {
    keys = null;
    hush();
    // Which voices are allowed changes how many of the focused birds this mode
    // can ask about at all, so the note and the ladder are rebuilt when it does.
    const askable = () =>
      pool ? narrow(soundMatchPool(voices === 'all' ? undefined : WITHOUT_CALLS), pool) : null;
    let setup = focusSetup(focus, askable(), len, t('quiz.soundMatch.setup.lacking'));
    len = setup.len;
    let lenRow = setup.empty ? el('span.focusslot', {}) : lengthField(len, (n) => (len = n), setup.lengths);
    const note = el('div.focusslot', {}, setup.note);
    wrap.replaceChildren(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.setup',
        {},
        el('h1', {}, t('common.mode.soundMatch')),
        el('p.blurb.meta', {}, t('quiz.soundMatch.setup.blurb')),
        note,
        lenRow,
        setup.empty ? null : newBirdsField(share, (v) => (share = v)),
        el(
          'div.field',
          {},
          el('div.prompt.label', {}, t('quiz.voices.label')),
          segmented(VOICES, voices, (v) => {
            voices = v;
            if (!focus) return;
            setup = focusSetup(focus, askable(), len, t('quiz.soundMatch.setup.lacking'));
            len = setup.len;
            note.replaceChildren(...(setup.note ? [setup.note] : []));
            const next = setup.empty
              ? el('span.focusslot', {})
              : lengthField(len, (n) => (len = n), setup.lengths);
            lenRow.replaceWith(next);
            lenRow = next;
          }),
          unlocked
            ? el(
                'div.hint',
                {},
                t('quiz.voices.unlocked', { pct: HARD_SOUND_MASTERY }),
              )
            : null,
        ),
        setup.empty
          ? emptyFocus(ctx)
          : el(
              'button.next.btn.btn-primary',
              { onclick: () => void start() },
              t('quiz.round.start'),
              icon('arrow-right', { size: 18 }),
            ),
      ),
    );
  }

  async function start(): Promise<void> {
    // A length that came off a focus is a fact about how many sparrows there
    // are, not a preference — remembering it would hand the next ordinary round
    // a number the learner never chose.
    if (ROUND_LENGTHS.includes(len)) await setRoundLength('match', len);
    await setNewBirdShare('match', share);
    await setSoundWithCalls(voices === 'all');
    hard = voices === 'all';
    session = await buildSoundMatchSession(
      new Date(),
      Math.random,
      hard ? undefined : WITHOUT_CALLS,
      firstRound ? ctx.params.done : undefined,
      len,
      pool ?? undefined,
    );
    firstRound = false;
    retryOn = await retryMisses();
    size = session.length;
    index = 0;
    correctCount = 0;
    missed = [];
    // The prompt is a photograph, and photographs live in the media pack. A
    // round of "photo not downloaded" placeholders would be unanswerable, so
    // say so plainly instead — exactly as Photo ID does.
    if (session.length && !(await imageUrl(session[0].photo.file))) {
      if (!disposed) renderNoPack();
      return;
    }
    render();
  }

  function render(): void {
    hush();
    keys = null;
    if (disposed) return;
    wrap.replaceChildren();
    if (!session.length) return renderEmpty();
    if (index >= session.length) return renderDone();
    renderQuestion(session[index]);
  }

  function renderQuestion(q: SoundMatchQuestion): void {
    const header = quizHeader(ctx, index, session.length, t('quiz.soundMatch.question.prompt'));
    const hero = speciesPhoto(q.photo, { alt: q.target.common, mirror: q.mirrored });
    hero.element.classList.add('hero');

    // Which voice both clips are in. Hidden until the answer is in: knowing you
    // are separating two songs rather than two calls is worth something, and it
    // is worth more once you have committed to an answer.
    const typeTag = el(
      'span.typetag.label',
      { title: t('quiz.soundMatch.question.typeTitle', { type: q.type }) },
      t('quiz.round.soundType', { type: q.type }),
    );
    typeTag.hidden = true;

    const nextBtn = el(
      'button.next.btn.btn-primary',
      {},
      t('quiz.round.next'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    nextBtn.hidden = true;
    nextBtn.addEventListener('click', () => {
      clearUndo();
      index += 1;
      render();
    });

    const opts = el('div.opts', {});
    const picks: HTMLButtonElement[] = [];
    const blocks: HTMLElement[] = [];
    const nameRows: HTMLElement[] = [];
    let answered = false;
    // Which way the round's tally moved, so undo can move it back.
    let wasCorrect = false;
    // A miss come back: answered for practice, never scored or undone.
    const comeback = index >= size;
    // Whether answering sent this question to the back of the round.
    let requeued = false;

    const undoBtn = undoButton(async () => {
      if (!answered || !(await undoAnswer())) return;
      haptic('undo');
      if (wasCorrect) correctCount -= 1;
      else missed.pop();
      if (requeued) session.pop();
      // Redrawn from `session[index]`, so both recordings come back in the same
      // two slots — A is still A.
      render();
    });

    q.clips.forEach((clip, i) => {
      const player = waveformPlayer(clip);
      players.push(player);
      const pick = el('button.pick.btn.btn-quiet', {
        'aria-label': t('quiz.soundMatch.question.pickLabel', { letter: LETTERS[i] }),
      }) as HTMLButtonElement;
      pick.textContent = LETTERS[i];
      picks.push(pick);
      pick.addEventListener('click', () => void settle(i));

      const nameRow = el('div.nmrow', {});
      nameRows.push(nameRow);
      const block = el(
        'div.opt',
        {},
        el('div.row', {}, pick, player.element),
        nameRow,
      );
      blocks.push(block);
      opts.append(block);
    });

    // The two birds are already named on this screen; this puts their voices on
    // one axis rather than two rows, opened on the type both clips are in.
    const compare = compareButton();

    const dontKnow = dontKnowButton(() => settle(-1));

    const actions: KeyActions = {
      choices: picks,
      next: nextBtn,
      undo: undoBtn,
      skip: dontKnow,
    };
    keys = actions;

    /**
     * Everything answering does. `chosen` is -1 for "I don't know": both strips
     * are still named, because naming them is the lesson, but neither pick is
     * marked wrong, since neither was made.
     */
    async function settle(chosen: number): Promise<void> {
      if (answered) return;
      answered = true;
      const correct = chosen === q.answer;
      haptic(correct ? 'right' : 'wrong');
      wasCorrect = correct;
      requeued = !correct && retryOn;
      if (requeued) session.push(reaskMatch(q));
      if (comeback) {
        // Nothing to tally.
      } else if (correct) correctCount += 1;
      else missed.push({ code: q.target.code, type: q.type });
      dontKnow.hidden = true;
      picks.forEach((b, i) => {
        b.disabled = true;
        if (i === chosen) b.classList.add(correct ? 'correct' : 'wrong');
        else if (i === q.answer) b.classList.add('answer');
        else b.classList.add('dim');
      });
      // Name both clips. The tick and the cross carry the outcome alongside the
      // colour, so the result is never colour alone.
      q.codes.forEach((code, i) => {
        const sp = getSpecies(code);
        const mine = i === q.answer;
        const row = nameRows[i];
        row.classList.add(mine ? 'right' : chosen === i ? 'missed' : 'other');
        row.append(
          mine
            ? icon('check', { size: 16 })
            : chosen === i
              ? icon('cross', { size: 16 })
              : icon('volume', { size: 15 }),
          el('span.nm', {}, sp?.common ?? code),
        );
        blocks[i].classList.add('shown');
      });
      // The bar has to count the question just answered, or the last question
      // of a round sits at 90% while its result is on screen.
      fillProgress(header, index, session.length);
      typeTag.hidden = false;
      // Both birds regardless of whether the answer was right: unlike the other
      // modes, this question has always been ABOUT a pair — the decoy is the
      // bird this one is most often taken for — so the comparison is on offer
      // either way.
      compare.offer(q.target.code, q.codes[1 - q.answer], q.type);
      // The bird they named: on a miss that is the decoy, which is exactly the
      // substitution the mix-up list and the pair drill are built out of. A skip
      // names neither, and stays out of that record: not knowing which of two
      // recordings it was is not mistaking one bird for the other.
      if (!comeback) {
        await gradeAnswer({
          code: q.target.code,
          skill: 'sound',
          rating: ratingFromCorrect(correct),
          picked: chosen < 0 ? undefined : q.codes[chosen],
          xp: correct ? XP_PER_CORRECT : 0,
        });
      }
      if (disposed) return;
      nextBtn.hidden = false;
      undoBtn.hidden = comeback;
      nextBtn.replaceChildren(
        index + 1 >= session.length
          ? t('quiz.round.finish')
          : t('quiz.round.next'),
        icon('arrow-right', { size: 18 }),
      );
    }

    tagSlot(header).append(
      ...(focus ? [focusTag(focus, size)] : []),
      ...(comeback ? [retryTag()] : []),
      ...(hard
        ? [
            el(
              'span.hardtag.label',
              { title: t('quiz.soundMatch.question.withCallsTitle') },
              t('quiz.round.withCalls'),
            ),
          ]
        : []),
      typeTag,
    );

    wrap.append(
      header,
      hero.element,
      // The credit names the photographer, never the bird.
      el('div.attrib.note', {}, photoCredit(q.photo, q.mirrored)),
      el(
        'div.who',
        {},
        el('span.nm', {}, q.target.common),
        el('span.sci', {}, q.target.sci),
      ),
      el('div.hint', {}, t('quiz.soundMatch.question.hint')),
      opts,
      dontKnow,
      keyLegend(actions),
      afterBar(undoBtn, nextBtn, compare.element),
    );
  }

  function renderEmpty(): void {
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el('div.big', {}, t('quiz.round.nothingToStudy')),
        el(
          'div.stat',
          {},
          hard ? t('quiz.soundMatch.empty.withCalls') : t('quiz.soundMatch.empty.songsOnly'),
        ),
        el(
          'button.next.btn.btn-quiet',
          { onclick: () => renderSetup() },
          t('quiz.round.changeOptions'),
        ),
      ),
    );
  }

  function renderNoPack(): void {
    wrap.replaceChildren(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el('div.big', {}, t('quiz.round.noPackTitle')),
        el('div.stat', {}, t('quiz.soundMatch.noPack.body')),
        el(
          'button.next.btn.btn-primary',
          { onclick: () => ctx.navigate('/you') },
          t('quiz.round.goToDownloads'),
        ),
      ),
    );
  }

  function renderDone(): void {
    const pct = Math.round((correctCount / size) * 100);
    const again = el(
      'button.next.btn.btn-primary',
      { onclick: () => void start() },
      t('quiz.round.again'),
      icon('arrow-right', { size: 18 }),
    ) as HTMLButtonElement;
    keys = { next: again };
    // The misses play where they stand, in the voice the question asked them
    // in; their speakers are hushed by the same teardown as the strips.
    const panel = missedList(missed, ctx);
    missSpeakers = panel?.speakers ?? [];
    wrap.append(
      el('div.bar', {}, backBtn(ctx)),
      el(
        'div.done',
        {},
        el(
          'div.big',
          {},
          t('quiz.round.correct', { scored: correctCount, total: size }),
        ),
        el(
          'div.stat',
          {},
          t('quiz.round.stat', { pct, xp: correctCount * XP_PER_CORRECT }),
        ),
        panel?.element ?? null,
        again,
        el(
          'button.next.btn.btn-quiet',
          { onclick: () => renderSetup() },
          t('quiz.round.changeOptions'),
        ),
        exitButton(ctx, Boolean(focus)),
      ),
    );
  }

  return () => {
    disposed = true;
    releaseKeys();
    // Leaving a round mid-answer takes the undo with it: the question it
    // belonged to is off the screen, so nothing may reach back to it.
    clearUndo();
    hush();
  };
};
